import { APP_INITIALIZER, InjectionToken, NgModule } from '@angular/core';
import { MsalGuard, MsalService } from '@azure/msal-angular';
import {
  ActionRequiredState,
  AppEnvironmentState,
  FeatureFlagConfigurationState,
  GeaMissingTranslationHandler,
  GeaTranslateHttpLoader,
  HAS_MSAL,
  HeaderConfig,
  LIBRARY_CONFIG,
  MonitoringService,
  PermissionsState,
  SseStreamService,
  UserState,
} from '@gea/digital-ui-lib';
import { NgxsModule } from '@ngxs/store';

import { HttpClient } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { USE_MY_ACCOUNT_V2_ORDER } from '@spartacus/order/root';
import {
  AppRoutingModule,
  CmsPageGuard,
  PageLayoutComponent,
  PageSlotModule,
} from '@spartacus/storefront';
import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfiguration,
} from '../environments/models/environment.model';
import { CustomModule } from './custom/custom.module';
import { CustomMonitoringService } from './gea/services/monitoring.service';
import { CustomSseStreamService } from './gea/services/sse-stream.service';
import { SpartacusModule } from './spartacus/spartacus.module';

export const HEADER_CONFIG = new InjectionToken<HeaderConfig>('HeaderConfig');

// <CHANGE ME> - Change the path to your files
export function HttpLoaderFactory(http: HttpClient, environment: EnvironmentConfiguration) {
  return new GeaTranslateHttpLoader(
    http,
    environment.localizationStorageURL,
    [],
    ['./assets/i18n/'],
  );
}

const isIframe = window !== window.parent && !window.opener;

@NgModule({
  imports: [
    SpartacusModule,
    PageSlotModule,
    RouterModule.forRoot(
      [
        {
          path: '',
          pathMatch: 'full',
          component: PageLayoutComponent,
          canActivate: [CmsPageGuard, MsalGuard],
        },
      ],
      { initialNavigation: !isIframe ? 'enabledNonBlocking' : 'disabled' },
    ),
    NgxsModule.forRoot(
      [
        UserState,
        ActionRequiredState,
        AppEnvironmentState,
        PermissionsState,
        FeatureFlagConfigurationState,
      ],
      {
        compatibility: {
          strictContentSecurityPolicy: true,
        },
      },
    ),
    AppRoutingModule,
    TranslateModule.forRoot({
      extend: true,
      defaultLanguage: 'en-US',
      useDefaultLang: false,
      loader: {
        deps: [HttpClient, ENVIRONMENT_CONFIG],
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: GeaMissingTranslationHandler,
      },
    }),
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    CustomModule,
  ],
  providers: [
    {
      provide: LIBRARY_CONFIG,
      useFactory: (env: EnvironmentConfiguration) => ({
        ...env,
        baseUrl: env.portal.baseURL ?? env.baseURL,
        backendInfoUrl: `${env.portal.baseURL ?? env.baseURL}geaid/v1/info`,
        legalDocumentsStorageURL: env.storageURL,
      }),
      deps: [ENVIRONMENT_CONFIG],
    },
    {
      provide: 'baseUrl',
      useFactory: (env: EnvironmentConfiguration) => env.portal.baseURL ?? env.baseURL,
      deps: [ENVIRONMENT_CONFIG],
    },
    {
      provide: 'storageURL',
      useFactory: (env: EnvironmentConfiguration) => env.localizationStorageURL,
      deps: [ENVIRONMENT_CONFIG],
    },
    {
      provide: 'appContentPath',
      useFactory: (env: EnvironmentConfiguration) => env.appContentStorageURL,
      deps: [ENVIRONMENT_CONFIG],
    },

    {
      provide: USE_MY_ACCOUNT_V2_ORDER,
      useValue: true,
    },
    {
      provide: HAS_MSAL,
      // useValue: false if cookie exists with name 'Authorization' else true
      useValue: !document.cookie
        .split(';')
        .some((item) => item.trim().startsWith('Authorization=')),
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [MsalService],
      useFactory: (msal: MsalService) => () => msal.instance.initialize(),
    },
    { provide: MonitoringService, useClass: CustomMonitoringService },
    { provide: SseStreamService, useClass: CustomSseStreamService },
  ],
})
export class AppModule {}
